import { ensureFreshToken } from 'services/tokenManager';

// Many parts of the app call the browser `fetch` directly (AskAsper services,
// appVersion, Login's fetchScope, etc.) and attach the token themselves via an
// `Authorization: Bearer <token>` header read from localStorage. Those bypass
// the axios request interceptor, so they would otherwise send a potentially
// stale token.
//
// This installs a one-time global `fetch` wrapper that, for any outgoing request
// that already carries a Bearer token, replaces it with a freshly-ensured token
// (renewing first if the current one is stale). Requests without a Bearer header
// (third-party analytics, Auth0 iframe traffic, etc.) are passed through
// untouched, so we never leak our token to endpoints that didn't already use it.
let installed = false;

export function installFetchAuthInterceptor(): void {
    if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') {
        return;
    }
    installed = true;

    const originalFetch = window.fetch.bind(window);

    const wrappedFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        try {
            const headers = new Headers(
                init?.headers ?? (input instanceof Request ? input.headers : undefined),
            );
            const existing = headers.get('Authorization');

            if (existing && existing.startsWith('Bearer ')) {
                const token = await ensureFreshToken();
                headers.set('Authorization', `Bearer ${token}`);

                if (input instanceof Request && !init) {
                    return originalFetch(new Request(input, { headers }));
                }
                return originalFetch(input, { ...init, headers });
            }
        } catch {
            // On any failure, fall back to the untouched request so behaviour is
            // never worse than before this wrapper existed.
        }
        return originalFetch(input, init);
    };

    window.fetch = wrappedFetch as typeof window.fetch;
}
